/**
 * Flexible Übergabezeit (Phase M, v2 Kapitel 18, Entscheidung #21).
 *
 * Erlaubt, den Übergabepunkt zwischen zwei direkt aufeinanderfolgenden
 * eigenen Schichten zu verschieben — z. B. Früh 09–14 / Spät 14–19 auf
 * 09–13:30 / 13:30–19, wenn die Kolleg:innen sich informell darauf geeinigt
 * haben. KEINE Gegenbestätigung nötig (Vertrauensbasis) — nur eine
 * informative Push an die Filialleitung. Einzige harte Server-Prüfung: die
 * neue Zeit muss innerhalb der Standort-Öffnungszeiten liegen, UND es muss
 * tatsächlich eine direkt angrenzende Schicht existieren (sonst 400).
 *
 * Start- und End-Zeit werden unabhängig voneinander geprüft/gesendet — nur
 * das tatsächlich geänderte Feld löst einen Request aus.
 */

import { useState, type FormEvent } from 'react';
import { useAdjustHandoverTime } from '../data/shifts';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  shift: {
    id: string;
    startTime: string;
    endTime: string;
    originalStartTime: string | null;
    originalEndTime: string | null;
  };
  onClose: () => void;
  onSaved: () => void;
}

export function HandoverTimeModal({ shift, onClose, onSaved }: Props) {
  const token = useAuth((s) => s.token);
  const adjust = useAdjustHandoverTime();
  const [startTime, setStartTime] = useState(shift.startTime);
  const [endTime, setEndTime] = useState(shift.endTime);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startChanged = startTime !== shift.startTime;
  const endChanged = endTime !== shift.endTime;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) return;
    if (!startChanged && !endChanged) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      if (startChanged) {
        await adjust.mutateAsync({ id: shift.id, field: 'start', time: startTime, token });
      }
      if (endChanged) {
        await adjust.mutateAsync({ id: shift.id, field: 'end', time: endTime, token });
      }
      hapticSuccess();
      onSaved();
    } catch (err) {
      hapticError();
      setError(
        err instanceof ApiError
          ? err.message
          : 'Übergabezeit konnte nicht angepasst werden — bitte erneut versuchen.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md border border-border/60">
        <h3 className="text-lg font-semibold mb-1">Übergabezeit anpassen</h3>
        <p className="text-xs text-slate-400 mb-4">
          Nur für den Übergabepunkt zu einer direkt angrenzenden Schicht — ohne
          Freigabe, nur eine Info an die Filialleitung. Die neue Zeit muss
          innerhalb der Standort-Öffnungszeiten liegen.
        </p>

        {(shift.originalStartTime || shift.originalEndTime) && (
          <p className="text-xs text-slate-500 mb-3">
            Ursprünglich geplant: {shift.originalStartTime ?? shift.startTime}–
            {shift.originalEndTime ?? shift.endTime}
          </p>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Start-Zeit</span>
            <input
              type="time"
              required
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="input"
            />
          </label>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">End-Zeit</span>
            <input
              type="time"
              required
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="input"
            />
          </label>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Abbrechen
            </button>
            <button type="submit" disabled={busy} className="btn w-full sm:w-auto">
              {busy ? 'Sende…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
